import Review, {ReviewChecklistItem, ReviewItem, ReviewStatus} from "../../model/Review";
import Model from "../../model/Model";
import IModelItem from "../../model/IModelItem";
import {
  Box,
  DialogActions,
  DialogContent,
  Table, TableBody,
  TableCell, TableHead, TableRow,
  Typography,
  useTheme
} from "@mui/material";
import React, {ReactNode, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import DialogButton from "../../components/form/DialogButton";
import Asset, {AssetCategory, AssetType, AssetTypeDef, listAssetTypeDefs} from "../../model/Asset";
import Liability from "../../model/Liability";
import Snapshot, {ISnapshotDetail, SnapshotDetail, SnapshotDetailTypename} from "../../model/Snapshot";
import {SnapshotEditDetail} from "../calc/networth/NetWorthSnapshotDialog";
import FormValidator from "../../components/form/FormValidator";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import INetWorth from "../../model/INetWorth";
import {LiabilityIcon, LiquidAssetIcon, OtherAssetIcon, PersonalAssetIcon, RealAssetIcon} from "../../styles/Icons";
import {globalColors} from "../../styles/globalStyles";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
  createUUID, dateToLocalFormat,
  getISODateFromDate, getISODateStartOfYear,
  isoToLocalDate,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../stores/StoreUtilities";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {UpdateAssetInput, UpdateLiabilityInput, UpdateReviewInput, UpdateSnapshotInput} from "../../API";
import {useResources} from "../../stores/ResourceProvider";
import AssetEditDialog from "../calc/networth/AssetEditDialog";
import LiabilityEditDialog from "../calc/networth/LiabilityEditDialog";
import {AddCircleRounded} from "@mui/icons-material";
import BasicMenuButton from "../../components/controls/BasicMenuButton";
import ModelStore from "../../stores/ModelStore";
import {compareAsc} from "date-fns";


const NetWorthReview = ({
  review,
  model,
  updatedAt,
  onChange,
  onChangeView,
  onClose,
                          onDecline,
  isDialog
} : {
  review: Review
  model: Model
  updatedAt: string
  onChange?(item: IModelItem): void
  onChangeView?(view: string): void
  onClose?(): void
  onDecline?(review: Review): void
  isDialog?: boolean
}) => {
  const [liquidAssets, setLiquidAssets] = useState<Asset[]>([])
  const [realAssets, setRealAssets] = useState<Asset[]>([])
  const [personalAssets, setPersonalAssets] = useState<Asset[]>([])
  const [otherAssets, setOtherAssets] = useState<Asset[]>([])
  const [liabilities, setLiabilities] = useState<Liability[]>([])
  const [baseSnap, setBaseSnap] = useState<Snapshot | undefined>()
  const [snapshot, setSnapshot] = useState<Snapshot | undefined>()
  const [snapshotEditItems, setSnapshotEditItems] = useState<SnapshotEditDetail[]>([])
  const [reviewDate, setReviewDate] = useState<Date | undefined>()
  const [managed, setManaged] = useState<boolean>(false)
  const [editAsset, setEditAsset] = useState<Asset | undefined>()
  const [editLiability, setEditLiability] = useState<Liability | undefined>()

  const {modelStore, userStore} = useResources()

  useEffect(() => {
    if (model) {
      setLiquidAssets(model.getAassetsByCategory(AssetCategory.LiquidInvestableAssets))
      setRealAssets(model.getAassetsByCategory(AssetCategory.RealEstateAndProperty))
      setPersonalAssets(model.getAassetsByCategory(AssetCategory.PersonalProperty))
      setOtherAssets(model.getAassetsByCategory(AssetCategory.OtherAssets))
      setLiabilities(model.liabilities)
    }

    if (review.snapshotId) {
      setReviewDate(isoToLocalDate(review.reviewDate))
      const snap = model.snapshots.find((s: Snapshot) => s.id === review.snapshotId)
      if (snap) {
        setSnapshot(snap)
        setSnapshotEditItems(snap.details.map((item: ISnapshotDetail) => new SnapshotEditDetail(item)))
      }
      const base = model.snapshots.find((s: Snapshot) => s.id === review.baseSnapId)
      if (base) {
        setBaseSnap(base)
      }
    }

    if (userStore.isCustomer && review.managed) {
      setManaged(true)
    }
  }, [model, updatedAt])

  const navigate = useNavigate()
  const theme = useTheme()

  const styles = {
    tr: {
      maxHeight: "32px !important",
    },
    th: {
      fontSize: 14,
      whiteSpace: "nowrap"
    },
    tb: {
      maxHeight: "32px !important",
      py: 0,
      whiteSpace: "nowrap"
    },
    accountIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#40642F",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    realEstateIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#7f5539",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    personalPropertyIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#b08968",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    otherPropertyIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#ddb892",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    liabilityIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#881E10",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
  }

  const handleUpdate = async (item: INetWorth, snapshotItem?: ISnapshotDetail) => {
    if (snapshotItem) {
      const index = snapshotEditItems.findIndex((detail: SnapshotEditDetail) => detail.id === snapshotItem.id && detail.typename === snapshotItem.typename)
      if (index >= 0) {
        snapshotEditItems[index] = new SnapshotEditDetail(snapshotItem)
        setSnapshotEditItems([...snapshotEditItems])
      } else {
        if (item instanceof Asset) {
          const asset = item as Asset
          switch (asset.assetCategory) {
            case AssetCategory.LiquidInvestableAssets:
              setLiquidAssets([...liquidAssets, asset])
              break
            case AssetCategory.RealEstateAndProperty:
              setRealAssets([...realAssets, asset])
              break
            case AssetCategory.PersonalProperty:
              setPersonalAssets([...personalAssets, asset])
              break
            case AssetCategory.OtherAssets:
              setOtherAssets([...otherAssets, asset])
              break
          }
        } else if (item instanceof Liability) {
          setLiabilities([...liabilities, item as Liability])
        }
        snapshotEditItems.push(new SnapshotEditDetail(snapshotItem))
        setSnapshotEditItems([...snapshotEditItems])
      }

      await handleSave()
    }

    if (onChange) {
      onChange(item)
    }
  }

  const handleSave = async () => {
    if (!snapshot) {
      return
    }

    const details = snapshotEditItems.map((item: SnapshotEditDetail) => {
      if (item.amount !== undefined) { // Budget item
        return new SnapshotDetail({id: item.id, typename: item.typename, balance: moneyToNumberFormat(item.balance, 0), amount: moneyToNumberFormat(item.amount, 0), schedule: item.schedule})
      } else {
        return new SnapshotDetail({id: item.id, typename: item.typename, balance: moneyToNumberFormat(item.balance, 0)})
      }
    })

    const input: UpdateSnapshotInput = {
      id: snapshot.id,
      details: JSON.stringify(details)
    }

    const update = await modelStore.updateSnapshot(input)
    if (update) {
      // Update net worth items if this is the latest snapshot
      const index = model.snapshots.findIndex((s: Snapshot) => s.id === update.id)
      if (index === 0) {
        // TODO: Move this to when the review is submitted
        updateNetworthFromSnapshot(update)
      }
    }

    await updateChecklist(ReviewItem.AssetsAndLiabilities, true)

    if (update && onChange) {
      onChange(update)
    }
  }

  const updateChecklist = async (item: ReviewItem, checked: boolean) => {
    if (review.checklist) {
      const index = review.checklist.findIndex((checklistItem: ReviewChecklistItem) => checklistItem.reviewItem === item)
      if (index >= 0) {
        review.checklist[index].completed = checked
        const input: UpdateReviewInput = {
          id: review.id,
          checklist: JSON.stringify(review.checklist),
          status: ReviewStatus.InProgress
        }

        const update = await userStore.updateReview(input)
      }
    }
  }

  const getAsset = (id: string) => {
    let asset: Asset | undefined

    asset = liquidAssets.find((a: Asset) => a.id === id)
    if (!asset) {
      asset = realAssets.find((a: Asset) => a.id === id)
    }
    if (!asset) {
      asset = personalAssets.find((a: Asset) => a.id === id)
    }
    if (!asset) {
      asset = otherAssets.find((a: Asset) => a.id === id)
    }
    return asset
  }

  const updateNetworthFromSnapshot = async (snapshot: Snapshot) => {
    const accountId = model.accountId
    const assetPromises: Promise<Asset | undefined>[] = []
    const liabilityPromises: Promise<Liability | undefined>[] = []
    snapshot.details.forEach((detail: ISnapshotDetail) => {
      if (detail.typename === "Liability") {
        const liability = liabilities.find((l: Liability) => l.id === detail.id)
        if (liability && liability.balance !== detail.balance) {
          const update: UpdateLiabilityInput = {
            id: detail.id,
            accountId: accountId,
            balance: detail.balance,
            balanceDate: getISODateFromDate(snapshot.date)
          }
          liabilityPromises.push(modelStore.updateLiability(update))
        }
      } else {
        const asset = getAsset(detail.id)
        if (asset && asset.balance !== detail.balance) {
          const update: UpdateAssetInput = {
            id: detail.id,
            accountId: accountId,
            balance: detail.balance,
            balanceDate: getISODateFromDate(snapshot.date)
          }
          // TODO: Decide on logic for priorEoYBalance and withdrawalsYTD
          // if (!review.managed && asset.assetTypeDef.isTaxableIRA) {
          //   update.priorEoYBalance = detail.balance
          //   update.withdrawalsYTD = 0
          // }
          assetPromises.push(modelStore.updateAsset(update))
        }
      }
    })
    await Promise.all(assetPromises)
    await Promise.all(liabilityPromises)
  }

  const handlePrev = async (event: any) => {
    await handleSave()
    if (onChangeView) {
      onChangeView("timeline")
    }
  }

  const handleNext = async (event: any) => {
    await handleSave()
    if (onChangeView) {
      onChangeView("budget")
    }
  }

  const handleCancel = async (event: any) => {
    await handleSave()
    const input: UpdateReviewInput = {
      id: review.id,
      status: ReviewStatus.InProgress
    }

    const update = await userStore.updateReview(input)
    if (update && onChange) {
      onChange(update)
    }
    if (update && onClose) {
      onClose()
    }
  }

  const handleDecline = async (event: any) => {
    if (onDecline) {
      onDecline(review)
    }
  }

  const handleChangeBalance = (item: INetWorth, value: string, editIndex: number) => {
    if (editIndex < 0) {
      const typename = (item instanceof Asset) ? "Asset" : "Liability"
      const editItem = new SnapshotEditDetail({id: item.id, typename: typename, balance: 0})
      snapshotEditItems.push(editItem)
      editIndex = snapshotEditItems.length - 1
    }
    snapshotEditItems[editIndex].balance = value
    setSnapshotEditItems([...snapshotEditItems])
  }

  const liquidAssetMenuOptions = listAssetTypeDefs().map((def: AssetTypeDef) => {
    return new Asset({
      description: def.label,
      assetCategory: AssetCategory.LiquidInvestableAssets,
      assetType: def.assetType
    })
  })

  const realEstateAndPropertyMenuOptions = [
    new Asset({description: "Primary Residence", assetCategory: AssetCategory.RealEstateAndProperty, assetType: AssetType.None}),
    new Asset({description: "Vacation Home", assetCategory: AssetCategory.RealEstateAndProperty, assetType: AssetType.None}),
    new Asset({description: "Other Property", assetCategory: AssetCategory.RealEstateAndProperty, assetType: AssetType.None}),
  ]

  const personalPropertyMenuOptions = [
    new Asset({description: "Computer Equipment", assetCategory: AssetCategory.PersonalProperty, assetType: AssetType.None}),
    new Asset({description: "Electronics", assetCategory: AssetCategory.PersonalProperty, assetType: AssetType.None}),
    new Asset({description: "Furniture", assetCategory: AssetCategory.PersonalProperty, assetType: AssetType.None}),
    new Asset({description: "Jewelry", assetCategory: AssetCategory.PersonalProperty, assetType: AssetType.None}),
    new Asset({description: "Tools", assetCategory: AssetCategory.PersonalProperty, assetType: AssetType.None}),
    new Asset({description: "Other Property", assetCategory: AssetCategory.PersonalProperty, assetType: AssetType.None}),
  ]

  const otherMenuAssetOptions = [
    new Asset({description: "Car #1", assetCategory: AssetCategory.OtherAssets, assetType: AssetType.None}),
    new Asset({description: "Car #2", assetCategory: AssetCategory.OtherAssets, assetType: AssetType.None}),
    new Asset({description: "Other Asset", assetCategory: AssetCategory.OtherAssets, assetType: AssetType.None}),
  ]


  const handleAddAsset = (index: number, assetMenuOptions: Asset[], assetGroup: Asset[]) => {
    const menuAsset = assetMenuOptions[index]
    if (model && menuAsset) {
      const latestSnapshot = model.snapshots[0]
      const asset = new Asset({
        id: createUUID(),
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        description: menuAsset.description,
        balance: 0,
        balanceDate: latestSnapshot.date,
        assetCategory: menuAsset.assetCategory,
        assetType: menuAsset.assetType,
        start: getISODateStartOfYear(),
        sortOrder: assetGroup.length > 0 ? assetGroup[assetGroup.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      setEditAsset(asset)
    }
  }

  const liabilityMenuOptions = [
    "Home Loan",
    "Car Loan",
    "Personal Loan",
    "Home Equity Line of Credit",
    "Other Liability"
  ]

  const handleAddLiability = (index: number) => {
    const description = liabilityMenuOptions[index]
    if (model && description && snapshot) {
      const liability = new Liability({
        id: createUUID(),
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        description: description,
        balance: 0,
        balanceDate: snapshot.date,
        start: getISODateStartOfYear(),
        sortOrder: liabilities.length > 0 ? liabilities[liabilities.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      setEditLiability(liability)
    }
  }

  const renderItem = (item: INetWorth) => {
    let icon
    if (item instanceof Asset) {
      if (managed && item.managed) {
        // Don't display managed items to customers
        return null
      }
      switch (item.assetCategory) {
        case AssetCategory.LiquidInvestableAssets:
          icon = <LiquidAssetIcon managed={item.managed}/>
          break
        case AssetCategory.RealEstateAndProperty:
          icon = <RealAssetIcon/>
          break
        case AssetCategory.PersonalProperty:
          icon = <PersonalAssetIcon/>
          break
        case AssetCategory.OtherAssets:
          icon = <OtherAssetIcon managed={item.managed}/>
          break
        default:
          icon = <LiquidAssetIcon/>
          break
      }
    } else {
      icon = <LiabilityIcon/>
    }

    const typename: SnapshotDetailTypename = item instanceof Asset ? "Asset" : item instanceof Liability ? "Liability" : ""
    let editIndex = snapshotEditItems.findIndex((detail: SnapshotEditDetail) => detail.id === item.id && detail.typename === typename)
    let balance = ""
    if (editIndex >= 0) {
      balance = snapshotEditItems[editIndex].balance
    } else if (item.startDate && (reviewDate && reviewDate.getTime() < item.startDate.getTime())) {
      // Don't display item if the snapshot date is before the item startDate
      return null
    }

    let baseBalance = 0
    if (baseSnap) {
      baseBalance = baseSnap.getSnapshotBalance(item.id, typename)
    }

    let changeColor = globalColors.iconContrast
    let changeIcon = <ArrowDropUpIcon/>
    let changePct = ""
    if (baseBalance > 0) {
      changePct = numberToPercentFormat((moneyToNumberFormat(balance) - baseBalance) / baseBalance, 2)
    } else if (!balance) {
      changeIcon = <span></span>
    }
    if (changePct == "0%") {
      changePct = ""
      changeIcon = <span></span>
    } else if (changePct.startsWith("-")) {
      changeColor = globalColors.negative
      changeIcon = <ArrowDropDownIcon/>
      changePct = changePct.substring(1)
    } else {
      changeColor = globalColors.liquidAsset
    }

    return (
      <TableRow key={item.id} sx={styles.tr}>
        <TableCell sx={styles.tb}>{icon}</TableCell>
        <TableCell sx={styles.tb}>{item.description}</TableCell>
        {/*<TableCell sx={styles.tb}>{item.owner ? item.owner.nickname : ""}</TableCell>*/}
        {/*<TableCell sx={styles.tb}>*/}
        {/*  <IconButton id={`${item.id}-edit-btn`}size="small"*/}
        {/*              sx={{paddingTop:0, paddingBottom:0}}*/}
        {/*              onClick={(event: any) => {*/}
        {/*                if (item instanceof Asset) {*/}
        {/*                  setEditAsset(item)*/}
        {/*                } else if (item instanceof Liability) {*/}
        {/*                  setEditLiability(item)*/}
        {/*                }*/}
        {/*              }}*/}
        {/*  >*/}
        {/*    <EditIcon sx={{width:18, height:18}}/>*/}
        {/*  </IconButton>*/}
        {/*</TableCell>*/}
        <TableCell sx={styles.tb}>
          {baseSnap &&
            <Box display="flex" flexGrow={0} width={130} mr={2}>
              <TextFieldValidator
                margin="none"
                name={`base-${item.id}`}
                label=""
                type="text"
                fullWidth
                variant="standard"
                value={numberToMoneyFormat(baseBalance, 0)}
                disabled={true}
                InputLabelProps={{shrink: false}}
              />
            </Box>
          }
        </TableCell>
        <TableCell sx={styles.tb}>
          <TextFieldValidator
            margin="none"
            name={item.id}
            label=""
            type="text"
            fullWidth
            variant="standard"
            required
            validators={{ required: false, isMoney: true }}
            value={balance}
            InputLabelProps={{shrink: false}}
            onChange={(event: any) => {
              handleChangeBalance(item, event.currentTarget.value, editIndex)
            }}
          />
        </TableCell>
        <TableCell sx={styles.tb}>
          <Box display="flex" p={0} whiteSpace="nowrap" color={changeColor}>
            {changeIcon} <Typography variant="body2" color={changeColor}>{changePct}</Typography>
          </Box>
        </TableCell>
      </TableRow>
    )
  }

  const renderCategory = (title: string, items: INetWorth[], typename: "Asset" | "Liability", itemName: string) => {
    let menuButton : ReactNode | null = null
    switch (typename) {
      case "Asset":
        if (itemName === "Liquid Asset") {
          menuButton = <BasicMenuButton
            icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
            label={`New ${itemName}`}
            options={liquidAssetMenuOptions.map((asset: Asset) => asset.description)}
            onClickOption={(index: number) => handleAddAsset(index, liquidAssetMenuOptions, liquidAssets)}
          />
        } else if (itemName === "Real Estate") {
          menuButton = <BasicMenuButton
            icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
            label={`New ${itemName}`}
            options={realEstateAndPropertyMenuOptions.map((asset: Asset) => asset.description)}
            onClickOption={(index: number) => handleAddAsset(index, realEstateAndPropertyMenuOptions, realAssets)}
          />
        } else if (itemName === "Personal Property") {
          menuButton = <BasicMenuButton
            icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
            label={`New ${itemName}`}
            options={personalPropertyMenuOptions.map((asset: Asset) => asset.description)}
            onClickOption={(index: number) => handleAddAsset(index, personalPropertyMenuOptions, personalAssets)}
          />
        } else if (itemName === "Other Asset") {
          menuButton = <BasicMenuButton
            icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
            label={`New ${itemName}`}
            options={otherMenuAssetOptions.map((asset: Asset) => asset.description)}
            onClickOption={(index: number) => handleAddAsset(index, otherMenuAssetOptions, otherAssets)}
          />
        }
        break
      case "Liability":
        menuButton = <BasicMenuButton
          icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
          label={`New ${itemName}`}
          options={liabilityMenuOptions}
          onClickOption={handleAddLiability}
        />
        break
    }

    const priorLabel =  baseSnap && (!snapshot || baseSnap.id !== snapshot.id) ?`${dateToLocalFormat(baseSnap.date, 'M/d/yy')} Balance` : 'Prior Balance'
    const newLabel = snapshot ? `${dateToLocalFormat(snapshot.date, 'M/d/yy')} Balance` : 'New Balance'

    return (
      <Box display="flex" width="100%" flexDirection="column" alignItems="flex-start" sx={{mb:2}}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={56} sx={styles.th}></TableCell>
              <TableCell sx={styles.th}><Typography variant="h5">{title}</Typography></TableCell>
              {/*<TableCell width={130} sx={styles.th}>Owner</TableCell>*/}
              {/*<TableCell width={60} sx={styles.th}></TableCell>*/}
              <TableCell width={178} sx={styles.th}>{priorLabel}</TableCell>
              <TableCell width={160} sx={styles.th}>{newLabel}</TableCell>
              <TableCell width={130} sx={styles.th}>Change %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item: INetWorth) =>
              renderItem(item)
            )}
          </TableBody>
        </Table>
        <Box ml={2.5}>
          {menuButton}
        </Box>
      </Box>
    )
  }

  const canFinishLater = onClose && !review.submittedOn
  const canDecline = onDecline && !review.managed
  const height = isDialog ? "calc(100vh - 220px)" : "calc(100vh - 140px)"

  return (
    <React.Fragment>
      <DialogContent sx={{ my:0, p:0, height:height, maxHeight:height, overflow:"scroll"}}>
        <Box sx={{display:"flex", flexDirection:"column", alignItems:"flex-start", mt:1, pl:0, pb:0, mb:1}}>
          <Typography variant="h2" color="primary" gutterBottom>Assets & Liabilities Review</Typography>
          <Typography variant="body2" gutterBottom>
            Please update your asset and liability account balances.
          </Typography>
        </Box>

        <Box sx={{width:"100%"}}>
          <FormValidator name="newWorthForm" id="netWorthForm">
            {renderCategory(managed ? "Liquid Assets (Held Away)" : "Liquid Assets", liquidAssets, "Asset", "Liquid Asset")}
            {renderCategory("Real Estate", realAssets, "Asset", "Real Estate")}
            {renderCategory("Personal Property", personalAssets, "Asset", "Personal Property")}
            {renderCategory("Other Assets", otherAssets, "Asset", "Other Asset")}
            {renderCategory("Liabilities", liabilities, "Liability", "Liability")}
          </FormValidator>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="center">
          <Box display="flex" justifyContent="flex-start">
            {canFinishLater &&
              <DialogButton variant="secondary" onClick={handleCancel}>
                Sign Out & Finish Later
              </DialogButton>
            }
            {canDecline &&
              <DialogButton variant="secondary" spacing="left" onClick={handleDecline}>
                Decline
              </DialogButton>
            }
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <DialogButton variant="secondary" onClick={handlePrev}>
            Prev
          </DialogButton>
          <DialogButton variant="primary" spacing="left" onClick={handleNext}>
            Next
          </DialogButton>
        </Box>
      </DialogActions>

      {editAsset &&
        <AssetEditDialog asset={editAsset} model={model} snapshot={snapshot}
                         open={true}
                         onSave={(update: Asset, snapshotItem?: ISnapshotDetail) => {
                           setEditAsset(undefined)
                           handleUpdate(update, snapshotItem)
                         }}
                         onClose={() => {
                           setEditAsset(undefined)
                         }}/>
      }
      {editLiability &&
        <LiabilityEditDialog liability={editLiability} model={model} snapshot={snapshot}
                             open={true}
                             onSave={(update: Liability, snapshotItem?: ISnapshotDetail) => {
                               setEditLiability(undefined)
                               handleUpdate(update, snapshotItem)
                             }}
                             onClose={() => {
                               setEditLiability(undefined)
                             }}/>
      }
    </React.Fragment>
  )
}

export default NetWorthReview